import { WhereItIs, fullLabel } from "../components/WhereItIs";
import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { scanAccept, scanRefuse, scanWarn } from "@/lib/scanFeedback";
import { RackLevel } from "@/components/places/RackLevel";
import { SoundToggle } from "@/components/SoundToggle";
import { ScanField } from "@/components/ScanField";
import { PutAwayDialog } from "@/components/PutAwayDialog";
import { AuthImg } from "@/components/AuthImg";
import { ScanLine, CheckCircle2, XCircle, MapPin, Volume2, VolumeX, AlertTriangle, CheckCheck, RotateCcw, Package, RefreshCw, Undo2 } from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";

const returnedDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); }
  catch { return ""; }
};

export default function PutawayPage() {
  const [locations, setLocations] = useState([]);
  const [binValue, setBinValue] = useState("");      // chosen bin (id or scanned code)
  const [binLabel, setBinLabel] = useState("");
  const [binLevel, setBinLevel] = useState(null);
  const [log, setLog] = useState([]);                // newest first
  const [last, setLast] = useState(null);            // {ok, ...} for the big flash
  const [params] = useSearchParams();

  // ---- Put-away queue: the parts physically waiting for a bin (returned parts surfaced first) ----
  const [queue, setQueue] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [returnedCount, setReturnedCount] = useState(0);
  const [noBinCount, setNoBinCount] = useState(0);
  const [scope, setScope] = useState(null);
  const [loadingQueue, setLoadingQueue] = useState(true);

  // RETURNS ONLY — his fix, 2026-10-08: this panel was built for returns and queried every
  // available part without a bin instead, so it became a browse list nobody asked for ("that's
  // the real bug"). The other number (sellable, no bin recorded) is a data gap, not a job, and
  // shows as one honest line below.
  const loadQueue = async () => {
    try {
      const { data } = await api.get("/putaway/queue", { params: { kind: "returns" } });
      setQueue(data.parts || []);
      setQueueTotal(data.total || 0);
      setReturnedCount(data.returned_count || 0);
      setNoBinCount(data.no_bin_recorded || 0);
      setScope(data.scope || null);
    } catch { /* view is best-effort */ }
    finally { setLoadingQueue(false); }
  };

  useEffect(() => { loadQueue(); }, []);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data.filter((l) => l.type !== "site"))).catch(() => {});
  }, []);

  // Pre-set the destination when arrived from a scanned-location "Put items here" deep-link.
  useEffect(() => {
    const d = params.get("dest");
    if (d) activateByScan(d);
    // eslint-disable-next-line
  }, []);

  // the level, big, whichever way the destination was set — scanned, picked or typed
  const levelReq = useRef(0);
  const levelFor = (code) => {
    const n = ++levelReq.current;
    api.get("/locations/resolve", { params: { scan: code } })
      .then((r) => { if (n === levelReq.current) setBinLevel(r.data.rack_level || null); })
      .catch(() => { if (n === levelReq.current) setBinLevel(null); });
  };

  const pickBin = (id) => {
    const l = locations.find((x) => x.id === id);
    setBinValue(id);
    setBinLevel(null);
    setBinLabel(l ? `${l.short_code} · ${fullLabel(l)}` : id);
    if (l?.short_code) levelFor(l.short_code);
  };

  useEffect(() => {
    if (!binValue || binLabel !== binValue) return undefined;   // typed into the fallback box
    const t = setTimeout(() => levelFor(binValue.trim()), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [binValue, binLabel]);

  // PRIMARY path — scan a bin OR a tote/pallet to activate the destination
  const activateByScan = async (code) => {
    if (!code) return;
    try {
      const { data } = await api.get("/locations/resolve", { params: { scan: code } });
      levelReq.current += 1;
      setBinValue(data.id);
      setBinLabel(`${data.short_code} · ${fullLabel(data)}`);
      setBinLevel(data.rack_level || null);
      scanAccept(`PUTTING INTO ${data.short_code}${data.rack_level ? ` · LEVEL ${data.rack_level.letter}` : ""}`);
      toast.success(`Destination set → ${data.short_code}`);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
    }
  };

  const doPutaway = async (code, meta) => {
    if (!code) return;
    if (!binValue) { toast.error("Set the destination bin first."); return; }
    try {
      const { data } = await api.post("/putaway", { part_scan: code, location_id: binValue,
                                                    provenance: prov("desk_putaway", meta) });
      if (data.already) {
        // Caught duplicate — a re-scan of a tag already in this bin. Amber, not red:
        // it's the scanner glitch, not an operator error. Distinct error tone.
        scanWarn(`${data.part?.short_code || "THAT PART"} IS ALREADY HERE`);
        setLast({ already: true, part: data.part, location: data.location, message: data.message });
        setLog((l) => [{ already: true, code, part: data.part, message: data.message, ts: Date.now() }, ...l].slice(0, 100));
      } else if (data.ok) {
        scanAccept(data.part?.short_code || "PUT AWAY");
        setLast({ ok: true, ...data });
        setLog((l) => [{ ok: true, code, part: data.part, ts: Date.now() }, ...l].slice(0, 100));
        loadQueue();
      } else {
        scanRefuse(data.message || "COULD NOT PUT IT AWAY");
        setLast({ ok: false, message: data.message });
        setLog((l) => [{ ok: false, code, message: data.message, ts: Date.now() }, ...l].slice(0, 100));
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      scanRefuse(msg || "COULD NOT PUT IT AWAY");
      setLast({ ok: false, message: msg });
      setLog((l) => [{ ok: false, code, message: msg, ts: Date.now() }, ...l].slice(0, 100));
    }
  };

  const okCount = log.filter((x) => x.ok).length;
  const dupCount = log.filter((x) => x.already).length;
  const errCount = log.filter((x) => !x.ok && !x.already).length;

  const finishBatch = () => {
    scanAccept(`${okCount} PUT AWAY`);
    const dup = dupCount > 0 ? ` · ${dupCount} re-read${dupCount === 1 ? "" : "s"} caught` : "";
    toast.success(`Batch done — ${okCount} part${okCount === 1 ? "" : "s"} put away → ${binLabel || "destination"}${dup}.`);
    setLog([]); setLast(null); setBinValue(""); setBinLabel(""); setBinLevel(null);
  };

  return (
    <div data-testid="putaway-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Put Away</h1>
          <p className="text-sm text-slate-500">Scan a bin or tote/pallet to set the destination, then scan parts into it — one or many, rapid-fire. Location logging only — no blocking pop-ups.</p>
        </div>
        <SoundToggle />
      </div>

      {/* ============ RETURNS WAITING FOR A BIN — returns only, drawn only when there IS one ============ */}
      {queueTotal > 0 && (
      <div data-testid="putaway-queue" className="border border-slate-200 bg-white mb-5">
        <div className="flex items-center justify-between bg-slate-900 text-white px-4 py-3">
          <div className="flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-amber-400" />
            <span className="font-display text-lg font-bold tracking-tight">Returns waiting for a bin</span>
            <span data-testid="putaway-queue-count"
              className="ml-1 rounded-full bg-amber-500 text-slate-900 font-mono text-sm font-black px-2.5 py-0.5">
              {queueTotal}
            </span>
            {returnedCount > 0 && (
              <span data-testid="putaway-queue-returned-count"
                className="rounded-full bg-[#B01E28] text-white font-mono text-xs font-bold px-2.5 py-0.5 flex items-center gap-1">
                <Undo2 className="w-3.5 h-3.5" />{returnedCount} returned
              </span>
            )}
            {scope && (
              <span data-testid="putaway-queue-scope"
                className={`rounded-full text-[11px] font-bold px-2.5 py-0.5 flex items-center gap-1 ${scope.site_scoped ? "bg-sky-500/20 text-sky-300" : "bg-white/10 text-slate-300"}`}>
                <MapPin className="w-3.5 h-3.5" />{scope.site_scoped ? `Scoped to ${scope.site_name}` : "All sites"}
              </span>
            )}
          </div>
          <Button data-testid="putaway-queue-refresh" onClick={loadQueue} variant="ghost"
            className="h-9 text-white hover:bg-white/10 rounded-lg">
            <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
          </Button>
        </div>
        {loadingQueue ? (
          <div className="p-6 text-center font-mono text-xs text-slate-400">Loading queue…</div>
        ) : queue.length === 0 ? (
          <div data-testid="putaway-queue-empty" className="p-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
            <div className="mt-2 font-display text-lg font-bold text-slate-700">Nothing waiting</div>
            <div className="text-sm text-slate-500">Every part has a home. Returned parts show up here the moment they come back.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-[46dvh] overflow-y-auto">
            {queue.map((p) => (
              <div key={p.id} data-testid={`putaway-queue-row-${p.short_code}`}
                className={`flex items-center gap-3 px-4 py-3 ${p.returned ? "bg-red-50/60" : ""}`}>
                <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                  {p.photo_file_id ? <AuthImg fileId={p.photo_file_id} thumb className="w-full h-full object-cover" alt={p.short_code} />
                    : <Package className="w-6 h-6 text-slate-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-base text-slate-900">{p.short_code}</span>
                    {p.returned && (
                      <span data-testid={`putaway-queue-returned-badge-${p.short_code}`}
                        className="rounded-md bg-[#B01E28] text-white text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 flex items-center gap-1">
                        <Undo2 className="w-3 h-3" />Returned
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 truncate">{p.name}{p.brand ? ` · ${p.brand}` : ""}</div>
                  {p.returned && (p.returned_from || p.returned_at) && (
                    <div data-testid={`putaway-queue-returned-from-${p.short_code}`} className="text-xs text-[#B01E28] font-medium mt-0.5">
                      {(() => {
                        const po = (p.returned_from?.po_reference || "").replace(/^PO\s+/i, "").trim();
                        return po ? `from PO ${po}`
                          : p.returned_from?.cart_number ? `from ${p.returned_from.cart_number}` : "returned";
                      })()}
                      {p.returned_reason_label ? ` · ${p.returned_reason_label}` : ""}
                      {p.returned_at ? ` · ${returnedDate(p.returned_at)}` : ""}
                    </div>
                  )}
                </div>
                <PutAwayDialog part={p} onDone={loadQueue}
                  trigger={
                    <Button data-testid={`putaway-queue-putaway-${p.short_code}`}
                      className="h-12 px-5 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white font-bold shrink-0">
                      <MapPin className="w-4 h-4 mr-1.5" />Put away
                    </Button>
                  } />
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* THE OTHER TRUTH, never hidden: a part we would SELL with no bin recorded. Nobody is
          holding it — it is on a shelf, just unrecorded — so it is a data gap, not a job. */}
      <a data-testid="putaway-no-bin-line" href="/parts?dq=needs_putaway"
        className="mb-5 h-12 flex items-center gap-2 border border-slate-200 bg-white px-4 text-sm hover:border-[#E0781A]">
        <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-slate-600">
          <span data-testid="putaway-no-bin-count" className="font-mono font-bold text-slate-900">{noBinCount}</span>
          {" "}sellable part{noBinCount === 1 ? "" : "s"} with no bin recorded
          {scope?.site_scoped ? ` · ${scope.site_name}` : ""}
        </span>
        <span className="ml-auto font-mono text-[11px] text-slate-400">open the list →</span>
      </a>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-4">
          {/* Destination — scan a bin OR tote/pallet to activate (PRIMARY); dropdown + manual kept as FALLBACK */}
          <div className="border border-slate-200 bg-white p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5 text-[#B01E28]" />Destination bin or container</Label>
            <ScanField testid="dest-scan" submitLabel="Set"
              placeholder="Scan a bin or tote/pallet to set the destination"
              onScan={activateByScan} />
            {binValue && <div data-testid="active-bin" className="mt-2 text-sm font-mono bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">ACTIVE → {binLabel}</div>}
            {binValue && <RackLevel level={binLevel} testid="active-bin-level" compact />}
            <details className="mt-3" data-testid="dest-fallback">
              <summary className="text-xs font-mono text-slate-500 cursor-pointer hover:text-slate-700">…or pick manually (fallback)</summary>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <Select value={binValue} onValueChange={pickBin}>
                  <SelectTrigger data-testid="bin-select" className="rounded-lg border-slate-200"><SelectValue placeholder="Pick a bin / container" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-72">{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.short_code} · {fullLabel(l)}</SelectItem>)}</SelectContent>
                </Select>
                <ScanTextInput testid="bin-scan-input" placeholder="…or type bin code"
                  value={binValue && !binLabel.includes("·") ? binValue : ""}
                  onChange={(v) => { setBinValue(v); setBinLabel(v); setBinLevel(null); }}
                  className="sm:max-w-[240px]" />
              </div>
            </details>
          </div>

          {/* Scan field — shared widget: camera + scan-gun + manual + QR-URL normalize */}
          <div className="border border-slate-200 bg-white p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Scan part tag</Label>
            {binValue ? (
              <ScanField testid="part-scan" submitLabel="Putaway" continuous
                placeholder="Scan or type part QR / barcode / FAS-code"
                onScan={doPutaway} />
            ) : (
              <p className="text-sm text-slate-400">Set the destination bin first to start scanning.</p>
            )}
          </div>

          {/* Big flash — green (put away) / amber (already here, caught dup) / red (error) */}
          {last && (() => {
            const tone = last.already ? "border-amber-500 bg-amber-50" : last.ok ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50";
            const Icon = last.already ? AlertTriangle : last.ok ? CheckCircle2 : XCircle;
            const color = last.already ? "text-amber-600" : last.ok ? "text-emerald-600" : "text-red-600";
            return (
              <div data-testid="putaway-flash" className={`border-2 p-6 text-center ${tone}`}>
                <Icon className={`w-12 h-12 mx-auto ${color}`} />
                {last.already ? (
                  <div className="mt-2" data-testid="putaway-flash-already">
                    <div className="font-display text-xl font-black text-amber-700">Already scanned here</div>
                    <div className="text-sm text-slate-700 mt-1">{last.part?.short_code} · {last.part?.name}</div>
                    <div className="font-mono text-xs text-amber-700 mt-1">Pull the tag away before scanning the next part.</div>
                  </div>
                ) : last.ok ? (
                  <div className="mt-2"><div className="font-display text-2xl font-black">{last.part.short_code}</div><div className="text-sm text-slate-700">{last.part.name} · {last.part.brand}</div><div className="text-xs text-emerald-700 mt-1 flex items-center justify-center gap-1">✓ got it → <WhereItIs location={last.location} size="sm" testid="putaway-where" /></div><div className="font-mono text-[11px] text-emerald-700">pull tag away, scan next</div></div>
                ) : (
                  <div className="mt-2 font-mono text-sm text-red-700">{last.message}</div>
                )}
              </div>
            );
          })()}

          {log.length > 0 && (
            <Button data-testid="putaway-finish" onClick={finishBatch} variant="outline"
              className="w-full rounded-lg border-slate-300 h-11">
              <CheckCheck className="w-4 h-4 mr-1.5" />Done — finish batch ({okCount} put away)
            </Button>
          )}
        </div>

        {/* Running log */}
        <div className="border border-slate-200 bg-white">
          <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex justify-between">
            <span>Session log</span>
            <span><span className="text-emerald-600" data-testid="ok-count">{okCount} ok</span> · <span className="text-amber-600" data-testid="dup-count">{dupCount} dup</span> · <span className="text-red-600" data-testid="err-count">{errCount} err</span></span>
          </div>
          <div className="max-h-[60dvh] overflow-y-auto">
            {log.length === 0 ? <div className="p-6 text-center font-mono text-xs text-slate-400">NO SCANS YET</div>
              : log.map((x, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-xs ${x.already ? "bg-amber-50" : x.ok ? "" : "bg-red-50"}`}>
                  {x.already ? <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" /> : x.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                  {x.already ? <span className="font-mono text-amber-700">{x.part?.short_code} <span className="text-amber-600">already here</span></span>
                    : x.ok ? <span className="font-mono">{x.part.short_code} <span className="text-slate-500">{x.part.name}</span></span>
                    : <span className="font-mono text-red-700 truncate">{x.code}: {x.message}</span>}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
